import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, serverTimestamp, arrayUnion, FieldValue, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { getAuth } from 'firebase/auth';

const COURSES_COLLECTION = 'courses';

// Get all courses
export const getAllCourses = async () => {
  try {
    const coursesRef = collection(db, COURSES_COLLECTION);
    const q = query(coursesRef, orderBy('order', 'asc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting courses:', error);
    throw error;
  }
};

// Get a single course by ID
export const getCourseById = async (id) => {
  try {
    const courseRef = doc(db, COURSES_COLLECTION, id);
    const courseDoc = await getDoc(courseRef);
    
    if (courseDoc.exists()) {
      const courseData = courseDoc.data();
      
      // Use modules from course document (which includes order) and get detailed data from subcollection
      const courseModules = courseData.modules || [];
      
      // Get detailed module data from subcollection and merge with order info
      const modulesRef = collection(courseRef, 'modules');
      const modulesSnapshot = await getDocs(modulesRef);
      const modulesMap = new Map();
      
      // Create a map of detailed module data
      await Promise.all(modulesSnapshot.docs.map(async moduleDoc => {
        const moduleData = moduleDoc.data();
        
        // Get lessons from subcollection
        const lessonsRef = collection(moduleDoc.ref, 'lessons');
        const lessonsSnapshot = await getDocs(lessonsRef);
        const lessons = lessonsSnapshot.docs.map(lessonDoc => ({
          id: lessonDoc.id,
          ...lessonDoc.data()
        }));

        modulesMap.set(moduleDoc.id, {
          id: moduleDoc.id,
          ...moduleData,
          lessons
        });
      }));
      
      // Merge course document modules (with order) with detailed subcollection data
      console.log(`📋 Course modules from document:`, courseModules);
      const modules = courseModules.map(courseModule => {
        const detailedModule = modulesMap.get(courseModule.id);
        const result = detailedModule ? {
          ...detailedModule,
          order: courseModule.order || 0
        } : {
          id: courseModule.id,
          title: courseModule.title,
          order: courseModule.order || 0,
          lessons: []
        };
        console.log(`🔗 Merged module ${courseModule.id}:`, result);
        return result;
      }).sort((a, b) => (a.order || 0) - (b.order || 0));
      
      console.log(`📊 Final sorted modules:`, modules.map(m => ({ id: m.id, title: m.title, order: m.order })));
      
      // Debug: Check if all modules have order values
      const modulesWithoutOrder = modules.filter(m => !m.order || m.order === 0);
      if (modulesWithoutOrder.length > 0) {
        console.warn('⚠️ Modules without order values:', modulesWithoutOrder.map(m => ({ id: m.id, title: m.title, order: m.order })));
      }

      return {
        id: courseDoc.id,
        ...courseData,
        modules
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting course:', error);
    throw error;
  }
};

// Create a new course
export const createCourse = async (courseData) => {
  try {
    const coursesRef = collection(db, COURSES_COLLECTION);
    const docRef = await addDoc(coursesRef, {
      title: courseData.title,
      category: courseData.category,
      description: courseData.description,
      thumbnailUrl: courseData.thumbnailUrl,
      order: courseData.order,
      isPublished: courseData.isPublished || false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    return {
      id: docRef.id,
      ...courseData
    };
  } catch (error) {
    console.error('Error creating course:', error);
    throw error;
  }
};

// Update a course
export const updateCourse = async (id, courseData) => {
  try {
    const courseRef = doc(db, COURSES_COLLECTION, id);
    await updateDoc(courseRef, {
      ...courseData,
      updatedAt: new Date().toISOString()
    });
    return {
      id,
      ...courseData
    };
  } catch (error) {
    console.error('Error updating course:', error);
    throw error;
  }
};

// Delete a course
export const deleteCourse = async (id) => {
  try {
    const courseRef = doc(db, COURSES_COLLECTION, id);
    await deleteDoc(courseRef);
    return id;
  } catch (error) {
    console.error('Error deleting course:', error);
    throw error;
  }
};

// Module functions
export const addModuleToCourse = async (courseId, moduleData) => {
  try {
    const courseRef = doc(db, 'courses', courseId);
    const moduleRef = collection(courseRef, 'modules');
    
    // Ensure required fields have default values
    const cleanModuleData = {
      title: moduleData.title || 'Untitled Module',
      moduleLength: moduleData.moduleLength || '',
      order: typeof moduleData.order === 'number' ? moduleData.order : 1,
      ...moduleData
    };
    
    // Add the module document
    const newModuleRef = await addDoc(moduleRef, {
      ...cleanModuleData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Get the created module with its ID
    const moduleDoc = await getDoc(newModuleRef);
    const newModule = {
      id: moduleDoc.id,
      ...moduleDoc.data()
    };

    // Update the course document to include the module reference
    // Ensure we have valid data for arrayUnion
    const moduleReference = {
      id: newModule.id,
      title: cleanModuleData.title,
      order: cleanModuleData.order
    };

    await updateDoc(courseRef, {
      modules: arrayUnion(moduleReference),
      updatedAt: serverTimestamp()
    });

    return newModule;
  } catch (error) {
    console.error('Error adding module:', error);
    throw error;
  }
};

export const updateModule = async (courseId, moduleId, moduleData) => {
  try {
    console.log(`🔧 updateModule called for courseId: ${courseId}, moduleId: ${moduleId}, data:`, moduleData);
    
    const moduleRef = doc(db, 'courses', courseId, 'modules', moduleId);
    
    // Filter out undefined values to avoid Firebase errors
    const cleanModuleData = Object.fromEntries(
      Object.entries(moduleData).filter(([_, value]) => value !== undefined)
    );
    console.log(`🧹 Cleaned module data:`, cleanModuleData);
    
    // Update the module document
    console.log(`📝 Updating module document in subcollection...`);
    await updateDoc(moduleRef, {
      ...cleanModuleData,
      updatedAt: serverTimestamp()
    });
    console.log(`✅ Module document updated successfully`);

    // Update the module reference in the course document
    console.log(`📝 Updating course document modules array...`);
    const courseRef = doc(db, 'courses', courseId);
    const courseDoc = await getDoc(courseRef);
    const courseData = courseDoc.data();
    
    if (!courseData) {
      throw new Error('Course document not found');
    }
    
    if (!courseData.modules || !Array.isArray(courseData.modules)) {
      console.warn('Course modules array is missing or invalid, initializing...');
      courseData.modules = [];
    }
    
    console.log(`📋 Current course modules:`, courseData.modules);
    
    const updatedModules = courseData.modules.map(module => 
      module.id === moduleId 
        ? { ...module, ...cleanModuleData }
        : module
    );
    
    console.log(`📋 Updated course modules:`, updatedModules);

    // Ensure we have valid data before updating
    const updateData = {
      modules: updatedModules,
      updatedAt: serverTimestamp()
    };
    
    console.log(`📝 Course update data:`, updateData);
    
    await updateDoc(courseRef, updateData);
    console.log(`✅ Course document updated successfully`);

    const result = { id: moduleId, ...cleanModuleData };
    console.log(`🎉 updateModule completed successfully:`, result);
    return result;
  } catch (error) {
    console.error('❌ Error updating module:', error);
    throw error;
  }
};

export const deleteModule = async (courseId, moduleId) => {
  try {
    const moduleRef = doc(db, 'courses', courseId, 'modules', moduleId);
    
    // Delete all lessons in the module
    const lessonsRef = collection(moduleRef, 'lessons');
    const lessonsSnapshot = await getDocs(lessonsRef);
    const deletePromises = lessonsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    // Delete the module document
    await deleteDoc(moduleRef);

    // Update the course document to remove the module reference
    const courseRef = doc(db, 'courses', courseId);
    const courseDoc = await getDoc(courseRef);
    const courseData = courseDoc.data();
    
    const updatedModules = courseData.modules.filter(module => module.id !== moduleId);

    await updateDoc(courseRef, {
      modules: updatedModules,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deleting module:', error);
    throw error;
  }
};

// Add a lesson to a module
export const addLessonToModule = async (courseId, moduleId, lessonData) => {
  try {
    console.log('Adding lesson to module:', { courseId, moduleId, lessonData });
    
    // Create the full path to the lessons collection
    const lessonsRef = collection(db, COURSES_COLLECTION, courseId, 'modules', moduleId, 'lessons');
    
    // Ensure all required fields are present and properly formatted
    const newLesson = {
      title: lessonData.title || '',
      videoUrl: lessonData.videoUrl || '',
      videoLength: lessonData.videoLength || '',
      description: lessonData.description || '',
      order: typeof lessonData.order === 'number' ? lessonData.order : 0,
      status: lessonData.status || 'draft',
      documents: Array.isArray(lessonData.documents) ? lessonData.documents : [],
      moduleId: moduleId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    console.log('Creating new lesson with data:', newLesson);
    const docRef = await addDoc(lessonsRef, newLesson);
    const lessonId = docRef.id;
    console.log('Created lesson with ID:', lessonId);

    // Get the created document to return complete data
    const lessonDoc = await getDoc(docRef);
    const lessonRecord = { id: lessonId, ...lessonDoc.data() };

    // Update the module's lessons array in the course document
    const courseRef = doc(db, COURSES_COLLECTION, courseId);
    const courseDoc = await getDoc(courseRef);
    const courseData = courseDoc.data();
    
    // Ensure modules array exists
    if (!courseData.modules || !Array.isArray(courseData.modules)) {
      console.warn('Course modules array is undefined or not an array, initializing...');
      courseData.modules = [];
    }
    
    const updatedModules = courseData.modules.map(module => {
      if (module.id === moduleId) {
        return {
          ...module,
          lessons: [...(module.lessons || []), lessonRecord]
        };
      }
      return module;
    });

    await updateDoc(courseRef, {
      modules: updatedModules,
      updatedAt: serverTimestamp()
    });

    return lessonRecord;
  } catch (error) {
    console.error('Error adding lesson:', error);
    throw error;
  }
};

// Update a lesson
export const updateLesson = async (courseId, moduleId, lessonId, lessonData) => {
  try {
    console.log('Updating lesson:', { courseId, moduleId, lessonId, lessonData });
    
    // Create the full path to the lesson document
    const lessonRef = doc(db, COURSES_COLLECTION, courseId, 'modules', moduleId, 'lessons', lessonId);

    // First check if the document exists
    const lessonDoc = await getDoc(lessonRef);
    if (!lessonDoc.exists()) {
      throw new Error(`Lesson with ID ${lessonId} does not exist`);
    }

    // Ensure all required fields are present and properly formatted
    const updatedData = {
      title: lessonData.title || '',
      videoUrl: lessonData.videoUrl || '',
      videoLength: lessonData.videoLength || '',
      description: lessonData.description || '',
      order: typeof lessonData.order === 'number' ? lessonData.order : 0,
      status: lessonData.status || 'draft',
      documents: Array.isArray(lessonData.documents) ? lessonData.documents : [],
      moduleId: moduleId,
      updatedAt: serverTimestamp()
    };

    console.log('Updating lesson with data:', updatedData);
    await updateDoc(lessonRef, updatedData);
    console.log('Updated lesson successfully');

    // Update the lesson in the module's lessons array in the course document
    const courseRef = doc(db, COURSES_COLLECTION, courseId);
    const courseDoc = await getDoc(courseRef);
    const courseData = courseDoc.data();
    
    // Remove any serverTimestamp() from lesson objects before updating the course document
    const cleanLesson = (lesson) => {
      const l = { ...lesson };
      if (l.updatedAt && typeof l.updatedAt === 'object' && l.updatedAt.serverTimestamp) {
        delete l.updatedAt;
      }
      if (l.createdAt && typeof l.createdAt === 'object' && l.createdAt.serverTimestamp) {
        delete l.createdAt;
      }
      return l;
    };

    const updatedModules = courseData.modules.map(module => {
      if (module.id === moduleId) {
        return {
          ...stripFieldValues(module),
          lessons: (module.lessons || []).map(lesson => 
            lesson.id === lessonId 
              ? stripFieldValues({ ...lesson, ...updatedData })
              : stripFieldValues(lesson)
          )
        };
      }
      return stripFieldValues(module);
    });

    await updateDoc(courseRef, {
      modules: updatedModules,
      updatedAt: serverTimestamp()
    });

    return { id: lessonId, ...updatedData };
  } catch (error) {
    console.error('Error updating lesson:', error);
    throw error;
  }
};

// Delete a lesson
export const deleteLesson = async (courseId, moduleId, lessonId) => {
  try {
    // Create the full path to the lesson document
    const lessonRef = doc(db, COURSES_COLLECTION, courseId, 'modules', moduleId, 'lessons', lessonId);

    // Delete the lesson document
    await deleteDoc(lessonRef);

    // Remove the lesson from the module's lessons array in the course document
    const courseRef = doc(db, COURSES_COLLECTION, courseId);
    const courseDoc = await getDoc(courseRef);
    const courseData = courseDoc.data();
    
    const updatedModules = courseData.modules.map(module => {
      if (module.id === moduleId) {
        return {
          ...module,
          lessons: (module.lessons || []).filter(lesson => lesson.id !== lessonId)
        };
      }
      return module;
    });

    await updateDoc(courseRef, {
      modules: updatedModules,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error deleting lesson:', error);
    throw error;
  }
}; 

// Toggle lesson completion status
export const toggleLessonCompletion = async (courseId, moduleId, lessonId, completed, userId = null) => {
  try {
    console.log('Toggling lesson completion:', { courseId, moduleId, lessonId, completed, userId });
    
    // Create the full path to the lesson document
    const lessonRef = doc(db, COURSES_COLLECTION, courseId, 'modules', moduleId, 'lessons', lessonId);

    // First check if the document exists
    const lessonDoc = await getDoc(lessonRef);
    if (!lessonDoc.exists()) {
      throw new Error(`Lesson with ID ${lessonId} does not exist`);
    }

    // Update the lesson completion status
    const updatedData = {
      completed: completed,
      completedAt: completed ? serverTimestamp() : null,
      updatedAt: serverTimestamp()
    };

    console.log('Updating lesson completion with data:', updatedData);
    await updateDoc(lessonRef, updatedData);
    console.log('Updated lesson completion successfully');

    // Update the lesson in the module's lessons array in the course document
    const courseRef = doc(db, COURSES_COLLECTION, courseId);
    const courseDoc = await getDoc(courseRef);
    const courseData = courseDoc.data();
    
    const updatedModules = courseData.modules.map(module => {
      if (module.id === moduleId) {
        return {
          ...stripFieldValues(module),
          lessons: (module.lessons || []).map(lesson => 
            lesson.id === lessonId 
              ? stripFieldValues({ ...lesson, ...updatedData })
              : stripFieldValues(lesson)
          )
        };
      }
      return stripFieldValues(module);
    });

    await updateDoc(courseRef, {
      modules: updatedModules,
      updatedAt: serverTimestamp()
    });

    // Update user progress if userId is provided
    if (userId) {
      try {
        await updateUserCourseProgress(userId, courseId, lessonId, completed);
        console.log('Updated user progress successfully');
      } catch (progressError) {
        console.error('Error updating user progress:', progressError);
        // Don't fail the main operation if progress update fails
      }
    }

    return { id: lessonId, ...updatedData };
  } catch (error) {
    console.error('Error toggling lesson completion:', error);
    throw error;
  }
};

// Get user progress for a specific course
export const getUserCourseProgress = async (userId, courseId) => {
  try {
    const userProgressRef = doc(db, 'userCourseProgress', `${userId}_${courseId}`);
    const progressDoc = await getDoc(userProgressRef);
    
    if (progressDoc.exists()) {
      return progressDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting user course progress:', error);
    throw error;
  }
};

// Update user progress for a course
export const updateUserCourseProgress = async (userId, courseId, lessonId, completed) => {
  try {
    const userProgressRef = doc(db, 'userCourseProgress', `${userId}_${courseId}`);
    const progressDoc = await getDoc(userProgressRef);
    
    let progressData;
    if (progressDoc.exists()) {
      progressData = progressDoc.data();
    } else {
      // Initialize progress data
      progressData = {
        userId,
        courseId,
        completedLessons: [],
        totalLessons: 0,
        progressPercentage: 0,
        lastUpdated: serverTimestamp(),
        createdAt: serverTimestamp()
      };
    }
    
    // Update completed lessons array
    if (completed) {
      if (!progressData.completedLessons.includes(lessonId)) {
        progressData.completedLessons.push(lessonId);
      }
    } else {
      progressData.completedLessons = progressData.completedLessons.filter(id => id !== lessonId);
    }
    
    // Get course data to calculate total lessons
    const courseData = await getCourseById(courseId);
    if (courseData && courseData.modules) {
      const totalLessons = courseData.modules.reduce((total, module) => {
        return total + (module.lessons?.length || 0);
      }, 0);
      
      progressData.totalLessons = totalLessons;
      progressData.progressPercentage = totalLessons > 0 
        ? Math.round((progressData.completedLessons.length / totalLessons) * 100)
        : 0;
    }
    
    progressData.lastUpdated = serverTimestamp();
    
    // Save to Firestore
    await setDoc(userProgressRef, progressData);
    
    return progressData;
  } catch (error) {
    console.error('Error updating user course progress:', error);
    throw error;
  }
};

// Get all user course progress
export const getAllUserCourseProgress = async (userId) => {
  try {
    const userProgressRef = collection(db, 'userCourseProgress');
    const q = query(userProgressRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const progressMap = {};
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      progressMap[data.courseId] = data;
    });
    
    return progressMap;
  } catch (error) {
    console.error('Error getting all user course progress:', error);
    throw error;
  }
};

const parseFirestoreDate = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/** All new-course and new-lesson feed items across published courses, newest first. */
const collectCourseContentItems = async () => {
  const published = (await getAllCourses()).filter((c) => c.isPublished);
  const courses = await Promise.all(
    published.map((c) => getCourseById(c.id).catch(() => null))
  );
  const items = [];

  for (const course of published) {
    const sortDate = parseFirestoreDate(course.createdAt || course.updatedAt);
    if (sortDate) {
      items.push({
        kind: 'course',
        sortDate,
        courseId: course.id,
        title: course.title,
        category: course.category || 'General',
        thumbnailUrl: course.thumbnailUrl,
        path: `/portal/courses/${course.id}`
      });
    }
  }

  for (const course of courses.filter(Boolean)) {
    for (const mod of course.modules || []) {
      for (const lesson of mod.lessons || []) {
        // Lessons are visible to clients regardless of status (CourseContent renders
        // all of them), and the editor saves every lesson as 'draft', so only skip
        // lessons explicitly hidden — never the default 'draft'.
        if (lesson.status === 'hidden') continue;
        const sortDate = parseFirestoreDate(lesson.createdAt || lesson.updatedAt);
        if (!sortDate) continue;
        items.push({
          kind: 'lesson',
          sortDate,
          courseId: course.id,
          courseTitle: course.title,
          category: course.category || 'General',
          thumbnailUrl: course.thumbnailUrl,
          moduleId: mod.id,
          lessonId: lesson.id,
          title: lesson.title,
          path: `/portal/courses/${course.id}/modules/${mod.id}/lessons/${lesson.id}`
        });
      }
    }
  }

  return items.sort((a, b) => b.sortDate - a.sortDate);
};

/** Newest published courses and non-draft lessons, merged for the dashboard feed. */
export const getRecentCoursesAndLessons = async (limit = 4) => {
  try {
    const items = await collectCourseContentItems();
    return items.slice(0, limit);
  } catch (error) {
    console.error('Error getting recent courses and lessons:', error);
    throw error;
  }
};

/** Feed items plus each course's latest content timestamp (course added or lesson added/updated). */
export const getCourseContentActivity = async () => {
  try {
    const items = await collectCourseContentItems();
    const latestByCourse = {};
    for (const item of items) {
      if (!(item.courseId in latestByCourse)) latestByCourse[item.courseId] = item.sortDate;
    }
    return { items, latestByCourse };
  } catch (error) {
    console.error('Error getting course content activity:', error);
    throw error;
  }
};

const getDocumentDownloadUrl = (doc) =>
  doc?.url || doc?.downloadURL || doc?.downloadUrl || doc?.fileUrl || '';

/** Flatten lesson documents across published courses, newest first. */
export const getRecentCourseUploads = async (limit = 8) => {
  try {
    const published = (await getAllCourses()).filter((c) => c.isPublished);
    const courses = await Promise.all(
      published.map((c) => getCourseById(c.id).catch(() => null))
    );
    const items = [];
    for (const course of courses.filter(Boolean)) {
      for (const mod of course.modules || []) {
        for (const lesson of mod.lessons || []) {
          // Match client visibility: lesson documents show regardless of the default
          // 'draft' status; only skip lessons explicitly hidden.
          if (lesson.status === 'hidden') continue;
          for (const doc of lesson.documents || []) {
            const url = getDocumentDownloadUrl(doc);
            if (!url || !doc.uploadedAt) continue;
            items.push({
              courseId: course.id,
              courseTitle: course.title,
              moduleId: mod.id,
              lessonId: lesson.id,
              lessonTitle: lesson.title,
              displayName: doc.displayName || doc.name,
              uploadedAt: doc.uploadedAt,
              url,
              type: doc.type
            });
          }
        }
      }
    }
    return items
      .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting recent course uploads:', error);
    throw error;
  }
};

// Upload course thumbnail image
export const uploadCourseThumbnail = async (file) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to upload files');
    }

    // Validate file type (only images)
    const allowedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type not allowed. Please upload JPEG, PNG, GIF, or WebP images only.');
    }

    // Validate file size (max 5MB for thumbnails)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 5MB for thumbnails.');
    }

    // Create unique filename
    const timestamp = Date.now();
    const fileName = `course_thumbnail_${timestamp}_${file.name}`;
    const storageRef = ref(storage, `course-thumbnails/${auth.currentUser.uid}/${fileName}`);

    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      thumbnailUrl: downloadURL,
      fileName: file.name,
      fileSize: file.size
    };
  } catch (error) {
    console.error('Error uploading course thumbnail:', error);
    throw error;
  }
};

// Utility to recursively strip Firestore FieldValue (like serverTimestamp) from objects/arrays
function stripFieldValues(obj) {
  if (Array.isArray(obj)) {
    return obj.map(stripFieldValues);
  } else if (obj && typeof obj === 'object') {
    // Firestore FieldValue objects have an internal _methodName property
    if (typeof obj._methodName === 'string') {
      return undefined;
    }
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        const stripped = stripFieldValues(value);
        if (stripped !== undefined) {
          result[key] = stripped;
        }
      }
    }
    return result;
  }
  return obj;
}

// Reorder lessons in a module
export const reorderLessons = async (courseId, moduleId, lessonIds) => {
  try {
    console.log('Reordering lessons:', { courseId, moduleId, lessonIds });
    
    // Get the current course data
    const courseRef = doc(db, COURSES_COLLECTION, courseId);
    const courseDoc = await getDoc(courseRef);
    const courseData = courseDoc.data();
    
    if (!courseData || !courseData.modules) {
      throw new Error('Course or modules not found');
    }
    
    // Find the module
    const moduleIndex = courseData.modules.findIndex(m => m.id === moduleId);
    if (moduleIndex === -1) {
      throw new Error('Module not found');
    }
    
    const module = courseData.modules[moduleIndex];
    if (!module.lessons) {
      throw new Error('No lessons found in module');
    }
    
    // Create a map of lesson IDs to their new order
    const newOrderMap = {};
    lessonIds.forEach((lessonId, index) => {
      newOrderMap[lessonId] = index + 1;
    });
    
    // Update individual lesson documents in the subcollection
    const lessonUpdatePromises = lessonIds.map(async (lessonId, index) => {
      try {
        const lessonRef = doc(db, COURSES_COLLECTION, courseId, 'modules', moduleId, 'lessons', lessonId);
        console.log(`Attempting to update lesson ${lessonId} at path: courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`);
        
        // Check if the lesson document exists first
        const lessonDoc = await getDoc(lessonRef);
        if (!lessonDoc.exists()) {
          console.error(`Lesson document ${lessonId} does not exist in subcollection`);
          return;
        }
        
        await updateDoc(lessonRef, {
          order: index + 1,
          updatedAt: serverTimestamp()
        });
        console.log(`Successfully updated lesson ${lessonId} order to ${index + 1}`);
      } catch (error) {
        console.error(`Failed to update lesson ${lessonId}:`, error);
        throw error;
      }
    });
    
    // Wait for all lesson document updates to complete
    await Promise.all(lessonUpdatePromises);
    
    // Update the order of lessons in the module array
    const updatedLessons = module.lessons.map(lesson => ({
      ...lesson,
      order: newOrderMap[lesson.id] || lesson.order
    }));
    
    // Sort lessons by their new order
    updatedLessons.sort((a, b) => a.order - b.order);
    
    // Update the module in the course document
    const updatedModules = [...courseData.modules];
    updatedModules[moduleIndex] = {
      ...module,
      lessons: updatedLessons
    };
    
    await updateDoc(courseRef, {
      modules: updatedModules,
      updatedAt: serverTimestamp()
    });
    
    console.log('Lessons reordered successfully in both subcollection and course document');
    return updatedLessons;
  } catch (error) {
    console.error('Error reordering lessons:', error);
    throw error;
  }
};

// Reorder modules in a course
export const reorderModules = async (courseId, moduleIds) => {
  try {
    console.log('Reordering modules:', { courseId, moduleIds });
    
    // Get the current course data
    const courseRef = doc(db, COURSES_COLLECTION, courseId);
    const courseDoc = await getDoc(courseRef);
    const courseData = courseDoc.data();
    
    if (!courseData || !courseData.modules) {
      throw new Error('Course or modules not found');
    }
    
    // Create a map of module IDs to their new order
    const newOrderMap = {};
    moduleIds.forEach((moduleId, index) => {
      newOrderMap[moduleId] = index + 1;
    });
    
    // Update individual module documents in the subcollection
    const moduleUpdatePromises = moduleIds.map(async (moduleId, index) => {
      try {
        const moduleRef = doc(db, COURSES_COLLECTION, courseId, 'modules', moduleId);
        console.log(`Attempting to update module ${moduleId} at path: courses/${courseId}/modules/${moduleId}`);
        
        // Check if the module document exists first
        const moduleDoc = await getDoc(moduleRef);
        if (!moduleDoc.exists()) {
          console.error(`Module document ${moduleId} does not exist in subcollection`);
          return;
        }
        
        await updateDoc(moduleRef, {
          order: index + 1,
          updatedAt: serverTimestamp()
        });
        console.log(`Successfully updated module ${moduleId} order to ${index + 1}`);
      } catch (error) {
        console.error(`Failed to update module ${moduleId}:`, error);
        throw error;
      }
    });
    
    // Wait for all module document updates to complete
    await Promise.all(moduleUpdatePromises);
    
    // Update the order of modules in the course document
    const updatedModules = courseData.modules.map(module => ({
      ...module,
      order: newOrderMap[module.id] || module.order
    }));
    
    // Sort modules by their new order
    updatedModules.sort((a, b) => a.order - b.order);
    
    await updateDoc(courseRef, {
      modules: updatedModules,
      updatedAt: serverTimestamp()
    });
    
    console.log('Modules reordered successfully in both subcollection and course document');
    return updatedModules;
  } catch (error) {
    console.error('Error reordering modules:', error);
    throw error;
  }
}; 